import { createPortal } from 'react-dom';
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  arrow,
  limitShift,
  inline
} from '@floating-ui/react-dom';
import { useEffect } from 'react';
import { useCallback } from 'react';
import { useContext } from 'react';
import { useState } from 'react';
import { useRef } from 'react';
import { createContext } from 'react';

// Create a context to track active modals
export const ModalContext = createContext({
  registerModal: () => { },
  unregisterModal: () => { },
  isModalActive: () => false
});

export const ModalProvider = ({ children }) => {
  const [activeModals, setActiveModals] = useState(new Set());
  const registerModal = useCallback(id => {
    setActiveModals(prev => {
      const newSet = new Set(prev);
      newSet.add(id);
      return newSet;
    });
  }, []);

  const unregisterModal = useCallback(id => {
    setActiveModals(prev => {
      const newSet = new Set(prev);
      newSet.delete(id);
      return newSet;
    });
  }, []);

  const isModalActive = useCallback(() => {
    return activeModals.size > 0;
  }, [activeModals]);

  return (
    <ModalContext.Provider value={{ registerModal, unregisterModal, isModalActive }}>
      {children}
    </ModalContext.Provider>
  );
};
export function CustomerPopover({
  children,
  content,
  placement = 'auto',
  className,
  contentClassName,
  showArrow = true,
  caretPosition = 'auto',
  open: controlledOpen,
  onOpenChange,
  borderColor = 'black',
  animate = true,
  usePortal = true,
  zIndex,
  onClickButton,
  caretClassName,
  /** Stretch trigger to fill parent (e.g. full mint bar); omit w-fit on root */
  fillReference = false,
  closeOnScroll = false
}) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const arrowRef = useRef(null);
  const [mounted, setMounted] = useState(false);
  const { isModalActive } = useContext(ModalContext);
  // Handle SSR
  useEffect(() => {
    setMounted(true);
  }, []);

  const open = controlledOpen !== undefined ? controlledOpen : uncontrolledOpen;
  const setOpen = useCallback(
    value => {
      if (onOpenChange) {
        onOpenChange(value);
      } else {
        setUncontrolledOpen(value);
      }
    },
    [onOpenChange]
  );

  // Calculate alignment based on caretPosition
  const getAlignmentFromCaretPosition = side => {
    if (caretPosition === 'auto') return undefined;

    // For top and bottom placements
    if (side === 'top' || side === 'bottom') {
      if (caretPosition === 'left') return 'start';
      if (caretPosition === 'right') return 'end';
      if (caretPosition === 'center') return undefined; // center is default
    }

    // For left and right placements
    if (side === 'left' || side === 'right') {
      if (caretPosition === 'left') return 'start';
      if (caretPosition === 'right') return 'end';
      if (caretPosition === 'center') return undefined; // center is default
    }

    return undefined;
  };

  // Determine the initial placement with alignment
  const getInitialPlacement = () => {
    if (placement === 'auto') return undefined;

    const basePlacement = placement;
    const side = basePlacement.split('-')[0];
    const alignment = getAlignmentFromCaretPosition(side);

    if (alignment) {
      return `${side}-${alignment}`;
    }

    return placement;
  };

  const {
    x,
    y,
    strategy,
    refs,
    middlewareData,
    placement: computedPlacement
  } = useFloating({
    placement: getInitialPlacement(),
    middleware: [
      offset(12),
      inline(),
      flip({
        fallbackAxisSideDirection: 'end',
        padding: 16,
        crossAxis: true
      }),
      shift({ padding: 16, limiter: limitShift() }),
      showArrow && arrow({ element: arrowRef })
    ].filter(Boolean),
    whileElementsMounted: autoUpdate
  });

  // Extract the placement parts
  const side = computedPlacement.split('-')[0];

  // Calculate arrow position
  const arrowX = middlewareData.arrow?.x;
  const arrowY = middlewareData.arrow?.y;

  // Calculate custom arrow position based on caretPosition
  const getCustomArrowPosition = () => {
    if (caretPosition === 'auto') return { x: arrowX, y: arrowY };

    const floatingRect = refs.floating.current?.getBoundingClientRect();
    const referenceRect = refs.reference.current?.getBoundingClientRect();

    if (!floatingRect || !referenceRect) return { x: arrowX, y: arrowY };

    let customX = arrowX;
    let customY = arrowY;

    // For top and bottom placements
    if (side === 'top' || side === 'bottom') {
      if (caretPosition === 'left') {
        customX = Math.max(16, Math.min(floatingRect.width - 16, referenceRect.width * 0.25));
      } else if (caretPosition === 'right') {
        customX = Math.max(
          16,
          Math.min(floatingRect.width - 16, floatingRect.width - referenceRect.width * 0.25)
        );
      }
    }

    // For left and right placements
    if (side === 'left' || side === 'right') {
      if (caretPosition === 'left') {
        customY = Math.max(16, Math.min(floatingRect.height - 16, referenceRect.height * 0.25));
      } else if (caretPosition === 'right') {
        customY = Math.max(
          16,
          Math.min(floatingRect.height - 16, floatingRect.height - referenceRect.height * 0.25)
        );
      }
    }

    return { x: customX, y: customY };
  };

  const { x: customArrowX, y: customArrowY } = getCustomArrowPosition();

  const staticSide = {
    top: 'bottom',
    right: 'left',
    bottom: 'top',
    left: 'right'
  }[side];

  const handleClick = useCallback(() => {
    setOpen(!open);
  }, [open, setOpen]);

  // Close on escape key
  useEffect(() => {
    if (!open) return;

    const handleEscape = e => {
      if (e.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, setOpen]);

  // Close on scroll (ignores scroll inside the popover panel)
  useEffect(() => {
    if (!open || !closeOnScroll) return;

    const handleScroll = event => {
      const scrollTarget = event.target;
      if (scrollTarget instanceof Node && refs.floating.current?.contains(scrollTarget)) {
        return;
      }
      setOpen(false);
    };

    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [open, closeOnScroll, setOpen, refs.floating]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;

    const handleOutsideClick = e => {
      // Don't close if a modal is active
      const ele = document.getElementById('instagram-error-modal');
      if (ele && ele.contains(e.target)) {
        return;
      }
      if (isModalActive()) {
        return;
      }

      if (
        refs.floating.current &&
        refs.reference.current &&
        !refs.floating.current.contains(e.target) &&
        !refs.reference.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [open, refs.floating, refs.reference, setOpen, isModalActive]);

  // Create the popover content
  const popoverContent = (
    <div
      ref={refs.setFloating}
      onClick={e => e.stopPropagation()}
      style={{
        position: strategy,
        top: y ?? 0,
        left: x ?? 0,
        zIndex: zIndex ? zIndex : 60,
        opacity: 0,
        transform: animate ? `scale(0.95)` : 'none',
        transition: animate ? 'opacity 150ms ease, transform 150ms ease' : 'none',
        ...(open && { opacity: 1, transform: 'scale(1)' }),
        borderColor: borderColor
      }}
      className={`rounded-md border border-black shadow-md ${contentClassName}
      `}
      data-position={computedPlacement}
      data-caret={caretPosition}
      data-portal={'mui-popover'}
    >
      {showArrow && (
        <div
          ref={arrowRef}
          className={`absolute w-3 h-3 -z-10 bg-white rotate-45 ${caretClassName ? caretClassName : ''}`}
          style={{
            left:
              customArrowX != null
                ? caretPosition === 'right'
                  ? `${customArrowX - 14}px`
                  : `${customArrowX}px`
                : '',
            top: customArrowY != null ? `${customArrowY}px` : '',
            right: customArrowX != null ? `${customArrowX}px` : '',
            bottom: '',
            [staticSide]: '-7px',

            ...(staticSide === 'right' && {
              borderTop: `1px solid ${borderColor ? borderColor : 'black'}`,
              borderRight: `1px solid ${borderColor ? borderColor : 'black'}`
            }),
            ...(staticSide === 'bottom' && {
              borderBottom: `1px solid ${borderColor ? borderColor : 'black'}`,
              borderRight: `1px solid ${borderColor ? borderColor : 'black'}`
            }),
            ...(staticSide === 'left' && {
              borderBottom: `1px solid ${borderColor ? borderColor : 'black'}`,
              borderLeft: `1px solid ${borderColor ? borderColor : 'black'}`
            }),
            ...(staticSide === 'top' && {
              borderTop: `1px solid ${borderColor ? borderColor : 'black'}`,
              borderLeft: `1px solid ${borderColor ? borderColor : 'black'}`
            })
          }}
        />
      )}
      {content}
    </div>
  );

  const rootClass = fillReference
    ? `${className || ''} flex h-full min-h-0 w-full min-w-0`
    : `${className || ''} w-fit`;
  const referenceClass = fillReference
    ? 'flex h-full w-full min-h-0 min-w-0 cursor-pointer items-center justify-center'
    : 'w-full';

  return (
    <div className={rootClass}>
      <div
        className={referenceClass}
        ref={refs.setReference}
        onClick={() => {
          handleClick(), onClickButton && onClickButton();
        }}
      >
        {children}
      </div>

      {mounted &&
        open &&
        (usePortal && typeof document !== 'undefined'
          ? createPortal(popoverContent, document.body)
          : popoverContent)}
    </div>
  );
}
